import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ScrollToTop from "@/components/feature/ScrollToTop";
import SchemaOrg from "@/components/feature/SchemaOrg";
import { aboutData } from "@/mocks/about";

export default function AboutPage() {
  return (
    <>
      <ScrollToTop />
      <SchemaOrg
        type="Organization"
        data={{
          name: "探花精选",
          url: "https://pkgogo.cn",
          description: aboutData.slogan,
          foundingDate: "2020",
          contactPoint: {
            "@type": "ContactPoint",
            email: aboutData.contact.email,
            telephone: aboutData.contact.phone,
            contactType: "customer service",
          },
        }}
      />
      <SchemaOrg
        type="FAQPage"
        data={{
          mainEntity: aboutData.faqs.map((faq) => ({
            "@type": "Question",
            name: faq.question,
            acceptedAnswer: {
              "@type": "Answer",
              text: faq.answer,
            },
          })),
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: "首页", href: "/" },
                { label: "关于我们" },
              ]}
            />
          </div>

          {/* Hero */}
          <section className="py-10 md:py-16 bg-background-100">
            <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
              <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-primary-50 mx-auto mb-5">
                <i className="ri-flower-line text-3xl text-primary-500"></i>
              </div>
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-3">
                关于探花精选
              </h1>
              <p className="text-lg text-primary-500 font-heading font-medium">{aboutData.slogan}</p>
            </div>
          </section>

          {/* Brand Introduction */}
          <section className="py-10 md:py-14">
            <div className="max-w-3xl mx-auto px-4 md:px-6">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6">
                品牌故事
              </h2>
              <div className="space-y-4 text-foreground-700 leading-relaxed">
                {aboutData.introduction.split("\n\n").map((paragraph, i) => (
                  <p key={i}>{paragraph}</p>
                ))}
              </div>
              <div className="mt-8 p-6 rounded-lg bg-background-100 border border-background-200/70">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-50 flex-shrink-0 mt-0.5">
                    <i className="ri-double-quotes-l text-primary-500"></i>
                  </div>
                  <p className="text-foreground-800 font-heading text-lg italic leading-relaxed">
                    {aboutData.mission}
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Core Values */}
          <section className="py-10 md:py-14 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6 text-center">
                核心价值观
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {aboutData.values.map((value) => (
                  <div
                    key={value.title}
                    className="p-6 rounded-lg bg-background-50 border border-background-200/70"
                  >
                    <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">
                      {value.title}
                    </h3>
                    <p className="text-sm text-foreground-500 leading-relaxed">
                      {value.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Milestones */}
          <section className="py-10 md:py-14">
            <div className="max-w-3xl mx-auto px-4 md:px-6">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6">
                发展历程
              </h2>
              <div className="relative">
                <div className="absolute left-4 top-0 bottom-0 w-px bg-background-200"></div>
                <div className="space-y-0">
                  {aboutData.milestones.map((milestone, index) => (
                    <div key={index} className="relative pl-12 pb-8 last:pb-0">
                      <div className="absolute left-2.5 top-1 w-3 h-3 rounded-full bg-primary-500 ring-4 ring-background-50"></div>
                      <span className="text-xs font-bold text-primary-500 uppercase tracking-wider">
                        {milestone.year}
                      </span>
                      <p className="mt-1 text-sm text-foreground-600 leading-relaxed">
                        {milestone.event}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Team */}
          <section className="py-10 md:py-14 bg-background-100">
            <div className="max-w-3xl mx-auto px-4 md:px-6">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6">
                我们的团队
              </h2>
              <p className="text-foreground-700 leading-relaxed">
                {aboutData.teamIntro}
              </p>
            </div>
          </section>

          {/* Testimonials */}
          <section className="py-10 md:py-14">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6 text-center">
                用户口碑
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {aboutData.testimonials.map((testimonial, index) => (
                  <div
                    key={index}
                    className="p-6 rounded-lg bg-background-50 border border-background-200/70"
                  >
                    <div className="flex items-center gap-1 mb-3">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <i key={star} className="ri-star-fill text-accent-500 text-sm"></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-4 italic">
                      "{testimonial.content}"
                    </p>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 font-bold text-sm">
                        {testimonial.name.charAt(0)}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-800">{testimonial.name}</p>
                        <p className="text-xs text-foreground-400">{testimonial.role}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Contact */}
          <section id="contact" className="py-10 md:py-14 bg-background-100">
            <div className="max-w-3xl mx-auto px-4 md:px-6">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6">
                联系我们
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="flex items-start gap-3 p-4 rounded-lg bg-background-50 border border-background-200/70">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-50 flex-shrink-0">
                    <i className="ri-mail-line text-primary-500"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-800">电子邮箱</p>
                    <p className="text-sm text-foreground-500 mt-0.5">{aboutData.contact.email}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 rounded-lg bg-background-50 border border-background-200/70">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-50 flex-shrink-0">
                    <i className="ri-wechat-line text-accent-500"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-800">微信公众号</p>
                    <p className="text-sm text-foreground-500 mt-0.5">{aboutData.contact.wechat}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 rounded-lg bg-background-50 border border-background-200/70">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary-50 flex-shrink-0">
                    <i className="ri-weibo-line text-secondary-500"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-800">微博</p>
                    <p className="text-sm text-foreground-500 mt-0.5">{aboutData.contact.weibo}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 rounded-lg bg-background-50 border border-background-200/70">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-50 flex-shrink-0">
                    <i className="ri-map-pin-line text-primary-500"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-800">公司地址</p>
                    <p className="text-sm text-foreground-500 mt-0.5">{aboutData.contact.address}</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section id="faq" className="py-10 md:py-14">
            <div className="max-w-3xl mx-auto px-4 md:px-6">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6">
                常见问题
              </h2>
              <div className="space-y-3">
                {aboutData.faqs.map((faq, index) => (
                  <details key={index} className="group rounded-lg border border-background-200/70 bg-background-50 overflow-hidden">
                    <summary className="flex items-center justify-between px-5 py-4 cursor-pointer list-none">
                      <span className="text-sm font-medium text-foreground-800 pr-4">{faq.question}</span>
                      <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-background-100 group-open:bg-primary-50 transition-colors">
                        <i className="ri-add-line text-sm text-foreground-500 group-open:hidden"></i>
                        <i className="ri-subtract-line text-sm text-primary-500 hidden group-open:block"></i>
                      </span>
                    </summary>
                    <div className="px-5 pb-4">
                      <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}